// Shared MDM / Dry Ration consumption helper — the ONE formula used by Fund
// Management, the cascade, MDCF, the Rice Running Report and the Dry Ration
// Report. Per class: cost = class dayCost, rice = class rice qty × count.
import { buildDayBreakdown } from "./mealPlan";
import { isMealDay, normalizeEntry } from "./dayKind";

export const CONS_CLASSES = ["balVatika", "primary", "upperPrimary", "highSchool"];
const zero = () => ({ balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 });

// Per-class amounts for one entry; null on holiday / No-MDM / no-menu days.
// Legacy shared-group rice (BV+P use the Primary-group card, UP+HS the Upper-group
// card): drGroupRice = on DR days only (old DR Report), groupRice = on every day (old UC).
export function dayAmounts(entry, { drGroupRice = false, groupRice = false } = {}) {
  const e = normalizeEntry(entry);
  if (!e || !isMealDay(e)) return null;
  const bk = buildDayBreakdown(e.date, e.attendance || {});
  const isDR = e.entryType === "dryration";
  const cost = zero(); const rice = zero(); const count = zero();
  // No menu for the date (e.g. Sunday): the day still lists, with Rs 0 / 0 kg.
  if (!bk?.perClass) {
    CONS_CLASSES.forEach((k) => { count[k] = Number(e.attendance?.[k]) || 0; });
    return { date: e.date, isDR, cost, rice, count };
  }
  const riceQty = (lines) => (lines || []).find((l) => l.item === "Rice")?.qty || 0;
  bk.perClass.forEach((c) => {
    let qty = riceQty(c.lines);
    if (groupRice || (isDR && drGroupRice)) qty = riceQty(c.group === "primary" ? bk.primaryLines : bk.upperLines);
    cost[c.key] = c.dayCost || 0;
    rice[c.key] = (qty * (c.count || 0)) / 1000;
    count[c.key] = c.count || 0;
  });
  return { date: e.date, isDR, cost, rice, count };
}

// Month split: { mdm, dr, total } each { cost, rice } per class, plus DR day rows.
export function splitConsumed(entries, opts) {
  const out = { mdm: { cost: zero(), rice: zero() }, dr: { cost: zero(), rice: zero() }, total: { cost: zero(), rice: zero() }, drDays: [] };
  (entries || []).forEach((entry) => {
    const d = dayAmounts(entry, opts);
    if (!d) return;
    const b = d.isDR ? out.dr : out.mdm;
    CONS_CLASSES.forEach((k) => {
      b.cost[k] += d.cost[k]; b.rice[k] += d.rice[k];
      out.total.cost[k] += d.cost[k]; out.total.rice[k] += d.rice[k];
    });
    if (d.isDR) out.drDays.push(d);
  });
  out.drDays.sort((a, b) => a.date.localeCompare(b.date));
  return out;
}

// Dry Ration Report data ({ days, totals }) in the shape the report renders.
export function dryRationReportData(entries, opts) {
  const { drDays } = splitConsumed(entries, opts);
  const totals = { days: drDays.length, grandRice: 0, grandCost: 0 };
  CONS_CLASSES.forEach((k) => { totals[k] = { rice: 0, cost: 0 }; });
  const days = drDays.map((d) => {
    const day = { date: d.date };
    CONS_CLASSES.forEach((k) => {
      day[k] = { count: d.count[k], rice: d.rice[k], cost: d.cost[k] };
      totals[k].rice += d.rice[k]; totals[k].cost += d.cost[k];
    });
    day.totals = {
      rice: CONS_CLASSES.reduce((s, k) => s + d.rice[k], 0),
      cost: CONS_CLASSES.reduce((s, k) => s + d.cost[k], 0),
    };
    totals.grandRice += day.totals.rice; totals.grandCost += day.totals.cost;
    return day;
  });
  return { days, totals };
}
